import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import lottie from 'lottie-web/build/player/lottie_light'
import { Button, buttonTheme } from 'storybook-ui'

import { resetErrorAction } from 'root-redux/actions/common'
import { getUserStatusAction } from 'root-redux/actions/user'
import { selectActionList, selectError } from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useActiveCohortIdentifier } from 'hooks/useActiveCohortIdentifier'

import {
  AnimationState,
  DEFAULT_ERROR_DESCRIPTION,
} from 'modules/purchase/constants'
import {
  CHARGE_WELLNESS_BUNDLE,
  MAKE_UPSELL,
} from 'modules/purchase/redux/actions/upsell'

import paymentErrorAnimation from 'assets/animation/paymentErrorAnimation.json'
import paymentSuccessAnimation from 'assets/animation/paymentSuccessAnimation.json'
import paymentWaitingAnimation from 'assets/animation/paymentWaitingAnimation.json'

import { CHECK_3D_SECURE, PURCHASE } from '../../redux/actions/common'
import { select3DSecureIframeUrl } from '../../redux/selects/common'
import { StyledPaymentWaitingModal as S } from './PaymentWaitingModal.styles'

type TProps = {
  isPaymentWaitingShown: boolean
  resetErrorCallback?: () => void
  successCallback?: () => void
  setIsPaymentWaitingShown: (value: boolean) => void
  buttonText?: string
  errorButtonText?: string
  successSubtitle?: string
}

export const PaymentWaitingModal: React.FC<TProps> = ({
  isPaymentWaitingShown,
  setIsPaymentWaitingShown,
  resetErrorCallback,
  successCallback,
  buttonText,
  errorButtonText,
  successSubtitle,
}) => {
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const uuid = useSelector(selectUUID)
  const fetchingActionsList = useSelector(selectActionList)
  const error = useSelector(selectError)
  const threeDSecureIframeURL = useSelector(select3DSecureIframeUrl)
  const [animationState, setAnimationState] = useState<AnimationState>(
    AnimationState.LOADING,
  )

  const { isLuvlyGiftCardCohort } = useActiveCohortIdentifier()

  const isPurchaseInProcess = useMemo(
    () =>
      fetchingActionsList.includes(PURCHASE) ||
      fetchingActionsList.includes(MAKE_UPSELL) ||
      fetchingActionsList.includes(CHECK_3D_SECURE) ||
      fetchingActionsList.includes(CHARGE_WELLNESS_BUNDLE),
    [fetchingActionsList],
  )

  const loadingAnimationRef = useRef<HTMLDivElement | null>(null)
  const completeAnimationRef = useRef<HTMLDivElement | null>(null)
  const errorAnimationRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isPurchaseInProcess) {
      setIsPaymentWaitingShown(true)
    }

    if (isPurchaseInProcess && loadingAnimationRef.current) {
      lottie.loadAnimation({
        container: loadingAnimationRef.current,
        animationData: paymentWaitingAnimation,
        loop: true,
      })
    }

    if (!isPurchaseInProcess && isPaymentWaitingShown && error) {
      setAnimationState(AnimationState.ERROR)
    }

    if (
      !isPurchaseInProcess &&
      isPaymentWaitingShown &&
      !error &&
      !threeDSecureIframeURL
    ) {
      setAnimationState(AnimationState.SUCCESS)
    }

    return () => lottie.destroy()
  }, [
    error,
    isPurchaseInProcess,
    isPaymentWaitingShown,
    setIsPaymentWaitingShown,
    threeDSecureIframeURL,
  ])

  const handleResetError = useCallback(() => {
    setIsPaymentWaitingShown(false)
    setAnimationState(AnimationState.LOADING)
    dispatch(resetErrorAction())

    resetErrorCallback && resetErrorCallback()
  }, [dispatch, resetErrorCallback, setIsPaymentWaitingShown])

  const handleSuccessButtonClick = useCallback(async () => {
    await dispatch(getUserStatusAction(uuid))
    setIsPaymentWaitingShown(false)

    successCallback && successCallback()
  }, [dispatch, uuid, successCallback, setIsPaymentWaitingShown])

  useEffect(() => {
    switch (animationState) {
      case AnimationState.SUCCESS:
        if (completeAnimationRef.current) {
          lottie.loadAnimation({
            container: completeAnimationRef.current,
            animationData: paymentSuccessAnimation,
            loop: false,
          })
        }
        break
      case AnimationState.ERROR:
        if (errorAnimationRef.current) {
          lottie.loadAnimation({
            container: errorAnimationRef.current,
            animationData: paymentErrorAnimation,
            loop: false,
          })
        }
        break
      default:
        if (loadingAnimationRef.current) {
          lottie.loadAnimation({
            container: loadingAnimationRef.current,
            animationData: paymentWaitingAnimation,
            loop: true,
          })
        }
        break
    }

    return () => lottie.destroy()
  }, [animationState])

  useEffect(() => {
    if (isPaymentWaitingShown && wrapperRef.current) {
      wrapperRef.current.focus()
    }
  }, [isPaymentWaitingShown])

  const renderErrorButton = () => {
    if (isLuvlyGiftCardCohort) {
      return (
        <Button
          data-testid="purchase-error-btn"
          type="button"
          onClick={handleResetError}
          theme={buttonTheme.LUVLY_SECONDARY}
        >
          {errorButtonText || t`actions.tryAgain`}
        </Button>
      )
    }

    return (
      <S.ErrorButton
        data-testid="purchase-error-btn"
        type="button"
        onClick={handleResetError}
      >
        {errorButtonText || t`actions.tryAgain`}
      </S.ErrorButton>
    )
  }

  return (
    <S.Wrapper isShown={isPaymentWaitingShown} ref={wrapperRef} tabIndex={-1}>
      <S.Content>
        <S.LottieContainer data-testid="purchase-outcome-container">
          {animationState === AnimationState.LOADING && (
            <>
              <S.Animation ref={loadingAnimationRef} />
              <S.Title>{t`purchase1.paymentWaiting.processing`}</S.Title>
              <S.Subtitle>
                {t`purchase1.paymentWaiting.processingPayment`}
              </S.Subtitle>
            </>
          )}
          {animationState === AnimationState.SUCCESS && (
            <>
              <S.Animation ref={completeAnimationRef} />
              <S.Title data-testid="purchase-success-txt">
                {t`purchase1.paymentWaiting.paymentWasSuccessful`}
              </S.Title>
              {successSubtitle && (
                <S.SuccessSubtitle>
                  <Trans i18nKey={successSubtitle} components={[<br />]} />
                </S.SuccessSubtitle>
              )}
              <S.SuccessButton
                data-testid="purchase-success-btn"
                type="button"
                onClick={handleSuccessButtonClick}
              >
                {buttonText || t`purchase1.paymentWaiting.toLoginPage`}
              </S.SuccessButton>
            </>
          )}
          {animationState === AnimationState.ERROR && (
            <>
              <S.Animation ref={errorAnimationRef} />
              <S.ErrorSubtitle>{t`purchase1.paymentWaiting.declined`}</S.ErrorSubtitle>
              <S.ErrorInfo>
                {error?.description || error || DEFAULT_ERROR_DESCRIPTION}
              </S.ErrorInfo>
              <S.List>
                <li>{t('purchase1.paymentWaiting.sufficientBalance')}</li>
                <li>{t('purchase1.paymentWaiting.noLimits')}</li>
                <li>{t('purchase1.paymentWaiting.contactBank')}</li>
              </S.List>
              {renderErrorButton()}
            </>
          )}
        </S.LottieContainer>
      </S.Content>
    </S.Wrapper>
  )
}
